const basePath = "components-spares/";

export const componentsSpares = {
  id: 5,
  category: "Components & Spares",

  filters: {
    Brand: { type: "checkbox" },
    Model: { type: "checkbox" },
    Type: { type: "checkbox" },
    Capacity: { type: "checkbox" },
    Interface: { type: "checkbox" },
    Speed: { type: "checkbox" },
    "Form Factor": { type: "checkbox" },
    Compatibility: { type: "checkbox" },
  },

  subcategories: [
    // ==========================
    // PROCESSORS
    // ==========================
    {
      name: "Intel Xeon Silver 4310 Processor",
      bestSeller: true,
      image: `${basePath}Intel_Xeon_Silver_4310.svg`,
      details: {
        Brand: "Intel",
        Model: "Xeon Silver 4310",
        Type: "Server Processor",
        Speed: "2.10 GHz base, 3.30 GHz turbo",
        "Form Factor": "LGA 4189 (Socket P+)",
        Compatibility: "3rd Gen Xeon Scalable platforms",
      },
      about: `
        <ul>
          <li>12 cores and 24 threads with an 18 MB cache — the volume choice for general-purpose virtualisation hosts.</li>
          <li>120 W TDP keeps it within the thermal envelope of standard 1U cooling.</li>
          <li>Eight DDR4-2666 memory channels per socket for balanced memory bandwidth.</li>
          <li>Supported in Dell PowerEdge R650/R750 and HPE ProLiant Gen10 Plus generations.</li>
        </ul>
      `,
    },
    {
      name: "Intel Xeon Gold 6338 Processor",
      bestSeller: true,
      image: `${basePath}Intel_Xeon_Gold_6338.svg`,
      details: {
        Brand: "Intel",
        Model: "Xeon Gold 6338",
        Type: "Server Processor",
        Speed: "2.00 GHz base, 3.20 GHz turbo",
        "Form Factor": "LGA 4189 (Socket P+)",
        Compatibility: "3rd Gen Xeon Scalable platforms",
      },
      about: `
        <ul>
          <li>32 cores and 64 threads with a 48 MB cache for dense consolidation ratios.</li>
          <li>Suited to VDI, large hypervisor clusters and in-memory database nodes.</li>
          <li>Supports up to 6 TB of memory per socket with the right DIMM population.</li>
          <li>205 W TDP — confirm the chassis is specified for high-TDP parts before ordering.</li>
        </ul>
      `,
    },
    {
      name: "AMD EPYC 7443P Processor",
      bestSeller: false,
      image: `${basePath}AMD_EPYC_7443P.svg`,
      details: {
        Brand: "AMD",
        Model: "EPYC 7443P",
        Type: "Server Processor",
        Speed: "2.85 GHz base, 4.00 GHz boost",
        "Form Factor": "Socket SP3",
        Compatibility: "AMD EPYC 7003 series platforms",
      },
      about: `
        <ul>
          <li>24 cores and 48 threads with a 128 MB L3 cache — unusually large for this core count.</li>
          <li>Single-socket-only part, which removes the second-socket licence cost on per-socket software.</li>
          <li>128 PCIe Gen4 lanes from one socket, ideal for NVMe-dense storage nodes.</li>
          <li>Eight DDR4-3200 memory channels.</li>
        </ul>
      `,
    },

    // ==========================
    // MEMORY
    // ==========================
    {
      name: "Samsung 32GB DDR4-3200 ECC RDIMM",
      bestSeller: true,
      image: `${basePath}Samsung_32GB_DDR4_RDIMM.svg`,
      details: {
        Brand: "Samsung",
        Model: "M393A4K40DB3-CWE",
        Type: "Server Memory",
        Capacity: "32 GB",
        Speed: "DDR4-3200 (PC4-25600)",
        "Form Factor": "288-pin RDIMM",
        Compatibility: "2nd/3rd Gen Xeon Scalable, EPYC 7002/7003",
      },
      about: `
        <ul>
          <li>Registered ECC memory — the standard module for mainstream server builds.</li>
          <li>Dual rank, 1.2 V, with on-die ECC correcting single-bit errors before they reach the OS.</li>
          <li>Populate in matched sets across all channels to avoid halving effective bandwidth.</li>
          <li>Compatible with Dell PowerEdge, HPE ProLiant and Lenovo ThinkSystem platforms of the same generation.</li>
        </ul>
      `,
    },
    {
      name: "Micron 64GB DDR4-3200 ECC LRDIMM",
      bestSeller: false,
      image: `${basePath}Micron_64GB_DDR4_LRDIMM.svg`,
      details: {
        Brand: "Micron",
        Model: "MTA36ASF8G72LZ-3G2",
        Type: "Server Memory",
        Capacity: "64 GB",
        Speed: "DDR4-3200 (PC4-25600)",
        "Form Factor": "288-pin LRDIMM",
        Compatibility: "2nd/3rd Gen Xeon Scalable",
      },
      about: `
        <ul>
          <li>Load-reduced DIMM — buffers address and control lines so more modules can run per channel.</li>
          <li>The route to 2 TB or more per server without moving to a larger chassis.</li>
          <li>Chosen for in-memory databases, large VDI hosts and analytics nodes.</li>
          <li>Do not mix LRDIMMs and RDIMMs in the same system.</li>
        </ul>
      `,
    },
    {
      name: "Kingston 16GB DDR5-4800 ECC RDIMM",
      bestSeller: false,
      image: `${basePath}Kingston_16GB_DDR5_RDIMM.svg`,
      details: {
        Brand: "Kingston",
        Model: "KSM48R40BS8KMM-16HMR",
        Type: "Server Memory",
        Capacity: "16 GB",
        Speed: "DDR5-4800 (PC5-38400)",
        "Form Factor": "288-pin RDIMM",
        Compatibility: "4th Gen Xeon Scalable, EPYC 9004",
      },
      about: `
        <ul>
          <li>DDR5 registered ECC for current-generation server platforms.</li>
          <li>On-module power management and two independent 32-bit sub-channels per DIMM.</li>
          <li>Roughly 50% more bandwidth per module than the DDR4 generation it replaces.</li>
          <li>Not backward compatible with DDR4 boards — check the platform generation first.</li>
        </ul>
      `,
    },

    // ==========================
    // HARD DRIVES
    // ==========================
    {
      name: "Western Digital Ultrastar DC HC550 18TB HDD",
      bestSeller: true,
      image: `${basePath}WD_Ultrastar_DC_HC550_18TB.svg`,
      details: {
        Brand: "Western Digital",
        Model: "Ultrastar DC HC550",
        Type: "Enterprise Hard Drive",
        Capacity: "18 TB",
        Interface: "SATA 6 Gb/s",
        Speed: "7200 RPM",
        "Form Factor": '3.5" LFF',
      },
      about: `
        <ul>
          <li>Helium-sealed enterprise drive rated for 24×7 operation at 550 TB/year workload.</li>
          <li>2.5 million hour MTBF with a five-year design life.</li>
          <li>512 MB cache and 7200 RPM spindle for capacity tiers and backup targets.</li>
          <li>Lower power per terabyte than air-filled drives of the same capacity.</li>
        </ul>
      `,
    },
    {
      name: "Toshiba MG09 16TB Enterprise HDD",
      bestSeller: false,
      image: `${basePath}Toshiba_MG09_16TB.svg`,
      details: {
        Brand: "Toshiba",
        Model: "MG09ACA16TE",
        Type: "Enterprise Hard Drive",
        Capacity: "16 TB",
        Interface: "SATA 6 Gb/s",
        Speed: "7200 RPM",
        "Form Factor": '3.5" LFF',
      },
      about: `
        <ul>
          <li>Helium-sealed CMR drive — conventional recording, so no write-amplification surprises under sustained load.</li>
          <li>Rated for 550 TB/year in 24×7 nearline duty.</li>
          <li>Suits NAS arrays, surveillance retention and archive tiers.</li>
          <li>Persistent write cache protects in-flight data during an unexpected power loss.</li>
        </ul>
      `,
    },
    {
      name: "Seagate Exos 7E10 8TB SAS HDD",
      bestSeller: false,
      image: `${basePath}Seagate_Exos_7E10_8TB.svg`,
      details: {
        Brand: "Seagate",
        Model: "Exos 7E10 ST8000NM018B",
        Type: "Enterprise Hard Drive",
        Capacity: "8 TB",
        Interface: "SAS 12 Gb/s",
        Speed: "7200 RPM",
        "Form Factor": '3.5" LFF',
      },
      about: `
        <ul>
          <li>Dual-port SAS for redundant paths in a controller-attached array.</li>
          <li>The right interface for HPE MSA, Dell PowerVault and similar dual-controller enclosures.</li>
          <li>Protection Information (T10-PI) support for end-to-end data integrity checking.</li>
          <li>Commonly ordered as an array expansion or a like-for-like replacement drive.</li>
        </ul>
      `,
    },

    // ==========================
    // SOLID STATE DRIVES
    // ==========================
    {
      name: "Samsung PM893 3.84TB SATA SSD",
      bestSeller: true,
      image: `${basePath}Samsung_PM893_3.84TB.svg`,
      details: {
        Brand: "Samsung",
        Model: "PM893",
        Type: "Enterprise SSD",
        Capacity: "3.84 TB",
        Interface: "SATA 6 Gb/s",
        Speed: "550 MB/s read, 520 MB/s write",
        "Form Factor": '2.5" SFF',
      },
      about: `
        <ul>
          <li>Read-intensive TLC SSD rated at 1 drive write per day over five years.</li>
          <li>Drop-in upgrade for SATA bays in existing servers — no controller change needed.</li>
          <li>Power-loss protection capacitors preserve in-flight writes.</li>
          <li>Typical use: boot volumes, read-heavy databases, VM datastores.</li>
        </ul>
      `,
    },
    {
      name: "Micron 7450 PRO 3.84TB NVMe SSD",
      bestSeller: true,
      image: `${basePath}Micron_7450_PRO_3.84TB.svg`,
      details: {
        Brand: "Micron",
        Model: "7450 PRO",
        Type: "Enterprise NVMe SSD",
        Capacity: "3.84 TB",
        Interface: "PCIe Gen4 x4 (NVMe)",
        Speed: "6800 MB/s read, 5300 MB/s write",
        "Form Factor": 'U.3 2.5"',
      },
      about: `
        <ul>
          <li>PCIe Gen4 NVMe — roughly ten times the sequential throughput of a SATA SSD.</li>
          <li>U.3 form factor is backward compatible with U.2 tri-mode backplanes.</li>
          <li>1 DWPD endurance for mixed read/write production workloads.</li>
          <li>Sub-100 microsecond latency suits transactional databases and virtualisation hosts.</li>
        </ul>
      `,
    },
    {
      name: "Kingston DC600M 1.92TB SATA SSD",
      bestSeller: false,
      image: `${basePath}Kingston_DC600M_1.92TB.svg`,
      details: {
        Brand: "Kingston",
        Model: "DC600M",
        Type: "Enterprise SSD",
        Capacity: "1.92 TB",
        Interface: "SATA 6 Gb/s",
        Speed: "560 MB/s read, 530 MB/s write",
        "Form Factor": '2.5" SFF',
      },
      about: `
        <ul>
          <li>Mixed-use SSD rated at 1 DWPD, so it tolerates write-heavy workloads a read-intensive drive would not.</li>
          <li>On-board power-loss protection and hardware AES-256 encryption.</li>
          <li>Consistent latency under sustained load, which matters more than peak numbers in production.</li>
          <li>Cost-effective capacity tier for virtualisation and logging.</li>
        </ul>
      `,
    },

    // ==========================
    // GRAPHICS & ACCELERATORS
    // ==========================
    {
      name: "NVIDIA RTX A4000 16GB Workstation GPU",
      bestSeller: true,
      image: `${basePath}NVIDIA_RTX_A4000.svg`,
      details: {
        Brand: "NVIDIA",
        Model: "RTX A4000",
        Type: "Workstation GPU",
        Capacity: "16 GB GDDR6 ECC",
        Interface: "PCIe 4.0 x16",
        "Form Factor": "Single-slot, full height",
        Compatibility: "Tower and rack workstations",
      },
      about: `
        <ul>
          <li>Single-slot card with 6144 CUDA cores — fits workstations where a dual-slot card will not.</li>
          <li>16 GB of ECC memory for CAD, rendering and simulation datasets.</li>
          <li>140 W draw from the PCIe slot plus one 6-pin connector.</li>
          <li>Certified drivers for SolidWorks, AutoCAD, Maya and V-Ray.</li>
        </ul>
      `,
    },
    {
      name: "NVIDIA Tesla T4 16GB Inference GPU",
      bestSeller: false,
      image: `${basePath}NVIDIA_Tesla_T4.svg`,
      details: {
        Brand: "NVIDIA",
        Model: "Tesla T4",
        Type: "Datacenter GPU",
        Capacity: "16 GB GDDR6",
        Interface: "PCIe 3.0 x16",
        "Form Factor": "Single-slot, low profile",
        Compatibility: "1U/2U rack servers",
      },
      about: `
        <ul>
          <li>70 W passively cooled card designed to run on server chassis airflow.</li>
          <li>Low-profile single slot, so several fit in a 2U server.</li>
          <li>Tensor cores accelerate inference and video transcoding workloads.</li>
          <li>Common choice for VDI graphics acceleration and AI inference at the edge.</li>
        </ul>
      `,
    },
    {
      name: "NVIDIA A100 40GB Tensor Core GPU",
      bestSeller: false,
      image: `${basePath}NVIDIA_A100_40GB.svg`,
      details: {
        Brand: "NVIDIA",
        Model: "A100 PCIe",
        Type: "Datacenter GPU",
        Capacity: "40 GB HBM2",
        Interface: "PCIe 4.0 x16",
        "Form Factor": "Dual-slot, full height",
        Compatibility: "GPU-capable rack servers",
      },
      about: `
        <ul>
          <li>Training-class accelerator with 1.5 TB/s of memory bandwidth.</li>
          <li>Multi-Instance GPU partitions one card into up to seven isolated instances.</li>
          <li>250 W passively cooled — requires a chassis rated for GPU airflow, such as a Supermicro or Dell GPU platform.</li>
          <li>Used for model training, HPC and large-scale analytics.</li>
        </ul>
      `,
    },

    // ==========================
    // CONTROLLERS & ADAPTERS
    // ==========================
    {
      name: "Dell PERC H750 RAID Controller",
      bestSeller: true,
      image: `${basePath}Dell_PERC_H750.svg`,
      details: {
        Brand: "Dell",
        Model: "PERC H750",
        Type: "RAID Controller",
        Interface: "PCIe 4.0 x8, SAS 12 Gb/s",
        Capacity: "8 GB NV cache",
        "Form Factor": "Low profile PCIe card",
        Compatibility: "Dell PowerEdge 15G servers",
      },
      about: `
        <ul>
          <li>Hardware RAID 0/1/5/6/10/50/60 with battery-backed non-volatile cache.</li>
          <li>Tri-mode support handles SAS, SATA and NVMe drives on the same controller.</li>
          <li>The cache module is what makes write-back RAID safe through a power cut — check it is present on any used unit.</li>
          <li>Managed through iDRAC or the OpenManage storage services.</li>
        </ul>
      `,
    },
    {
      name: "HPE Smart Array P408i-a SR Gen10",
      bestSeller: false,
      image: `${basePath}HPE_Smart_Array_P408i-a.svg`,
      details: {
        Brand: "HPE",
        Model: "Smart Array P408i-a SR Gen10",
        Type: "RAID Controller",
        Interface: "PCIe 3.0 x8, SAS 12 Gb/s",
        Capacity: "2 GB flash-backed cache",
        "Form Factor": "Modular (AROC)",
        Compatibility: "HPE ProLiant Gen10 servers",
      },
      about: `
        <ul>
          <li>Modular controller that occupies the dedicated AROC slot rather than a PCIe slot.</li>
          <li>Flash-backed write cache with a supercapacitor rather than a battery — no scheduled replacement.</li>
          <li>Supports RAID 0/1/5/6/10/50/60 across 8 internal ports.</li>
          <li>Standard fitment in DL360 and DL380 Gen10 servers.</li>
        </ul>
      `,
    },
    {
      name: "Broadcom 9500-8i Tri-Mode HBA",
      bestSeller: false,
      image: `${basePath}Broadcom_9500-8i_HBA.svg`,
      details: {
        Brand: "Broadcom",
        Model: "HBA 9500-8i",
        Type: "Host Bus Adapter",
        Interface: "PCIe 4.0 x8, SAS/SATA/NVMe",
        "Form Factor": "Low profile PCIe card",
        Compatibility: "Standard PCIe 4.0 server slots",
      },
      about: `
        <ul>
          <li>Pass-through HBA rather than a RAID card — presents drives directly to the operating system.</li>
          <li>The correct choice for software-defined storage: ZFS, Ceph, Storage Spaces Direct and vSAN.</li>
          <li>Tri-mode connectivity supports SAS, SATA and NVMe on the same ports.</li>
          <li>Using a RAID controller in place of an HBA hides drive health from these systems — worth getting right.</li>
        </ul>
      `,
    },
    {
      name: "Intel X710-DA2 Dual-Port 10GbE NIC",
      bestSeller: true,
      image: `${basePath}Intel_X710-DA2.svg`,
      details: {
        Brand: "Intel",
        Model: "X710-DA2",
        Type: "Network Adapter",
        Interface: "PCIe 3.0 x8",
        Speed: "2 x 10 GbE SFP+",
        "Form Factor": "Low profile PCIe card",
        Compatibility: "Standard PCIe server slots",
      },
      about: `
        <ul>
          <li>Dual 10 GbE SFP+ ports with broad driver support across VMware, Windows Server and Linux.</li>
          <li>SR-IOV and hardware offloads reduce host CPU overhead on virtualisation hosts.</li>
          <li>Ships with both full-height and low-profile brackets.</li>
          <li>Requires SFP+ transceivers or DAC cables, which are ordered separately.</li>
        </ul>
      `,
    },
    {
      name: "NVIDIA ConnectX-5 25GbE Adapter",
      bestSeller: false,
      image: `${basePath}NVIDIA_ConnectX-5_25GbE.svg`,
      details: {
        Brand: "NVIDIA (Mellanox)",
        Model: "ConnectX-5 EN MCX512A",
        Type: "Network Adapter",
        Interface: "PCIe 3.0 x16",
        Speed: "2 x 25 GbE SFP28",
        "Form Factor": "Low profile PCIe card",
        Compatibility: "Standard PCIe server slots",
      },
      about: `
        <ul>
          <li>25 GbE with RDMA over Converged Ethernet for low-latency storage and cluster traffic.</li>
          <li>Hardware offload for overlay networks (VXLAN, NVGRE) keeps CPU cost flat as throughput rises.</li>
          <li>Standard adapter for hyper-converged clusters and NVMe-over-Fabrics targets.</li>
          <li>Backward compatible with 10 GbE switching.</li>
        </ul>
      `,
    },

    // ==========================
    // POWER & OPTICS
    // ==========================
    {
      name: "Dell 750W Hot-Plug Redundant PSU",
      bestSeller: false,
      image: `${basePath}Dell_750W_PSU.svg`,
      details: {
        Brand: "Dell",
        Model: "750W Platinum Hot-Plug",
        Type: "Server Power Supply",
        Capacity: "750 W",
        "Form Factor": "Hot-plug module",
        Compatibility: "Dell PowerEdge R640/R740/R650/R750",
      },
      about: `
        <ul>
          <li>80 PLUS Platinum efficiency — around 94% at half load.</li>
          <li>Hot-pluggable, so a failed unit is replaced without taking the server down.</li>
          <li>Fit two per chassis for genuine A/B feed redundancy.</li>
          <li>Match the wattage across both bays; mismatched supplies disable redundancy.</li>
        </ul>
      `,
    },
    {
      name: "HPE 800W Flex Slot Platinum PSU",
      bestSeller: false,
      image: `${basePath}HPE_800W_PSU.svg`,
      details: {
        Brand: "HPE",
        Model: "800W Flex Slot Platinum Hot Plug",
        Type: "Server Power Supply",
        Capacity: "800 W",
        "Form Factor": "Flex Slot hot-plug module",
        Compatibility: "HPE ProLiant Gen10 / Gen10 Plus",
      },
      about: `
        <ul>
          <li>Common spare across the ProLiant Gen10 range — DL360, DL380 and ML350.</li>
          <li>80 PLUS Platinum rated with active power factor correction.</li>
          <li>Hot-swappable from the rear of the chassis with no tools.</li>
          <li>A frequent failure item on used servers and worth carrying as a spare.</li>
        </ul>
      `,
    },
    {
      name: "Cisco SFP-10G-SR Transceiver",
      bestSeller: true,
      image: `${basePath}Cisco_SFP-10G-SR.svg`,
      details: {
        Brand: "Cisco",
        Model: "SFP-10G-SR",
        Type: "Optical Transceiver",
        Interface: "SFP+",
        Speed: "10 Gb/s",
        "Form Factor": "SFP+ module",
        Compatibility: "Cisco Catalyst and Nexus switching",
      },
      about: `
        <ul>
          <li>10GBASE-SR short-reach optic for multimode fibre up to 300 m on OM3.</li>
          <li>LC duplex connector — pair with an LC-LC multimode patch cord.</li>
          <li>Coded for Cisco platforms; third-party optics may be rejected unless the switch is configured to accept them.</li>
          <li>Hot-swappable with digital optical monitoring for link diagnostics.</li>
        </ul>
      `,
    },
    {
      name: "Generic QSFP28 100G SR4 Transceiver",
      bestSeller: false,
      image: `${basePath}QSFP28_100G_SR4.svg`,
      details: {
        Brand: "Multi-vendor",
        Model: "QSFP28-100G-SR4",
        Type: "Optical Transceiver",
        Interface: "QSFP28",
        Speed: "100 Gb/s",
        "Form Factor": "QSFP28 module",
        Compatibility: "Coded to order for Cisco, Arista, Juniper, HPE",
      },
      about: `
        <ul>
          <li>100 Gb/s over multimode fibre to 100 m on OM4, using four 25 Gb/s lanes.</li>
          <li>MPO-12 connector — requires an MPO trunk rather than an LC patch cord.</li>
          <li>Supplied coded for the target switch platform; specify the vendor when ordering.</li>
          <li>Also breaks out to 4 x 25 GbE with the appropriate fan-out cable.</li>
        </ul>
      `,
    },
  ],
};
